import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, MapPin, Grid3X3, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${id}`);
        setProject(response.data);
      } catch (error) {
        console.error("Error fetching project:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="section-padding">
        <div className="container-custom">
          <div className="aspect-video skeleton rounded-xl mb-8" />
          <div className="h-10 skeleton rounded w-1/2 mb-4" />
          <div className="h-6 skeleton rounded w-1/4" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="section-padding">
        <div className="container-custom text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h1>
          <Link to="/projects">
            <Button>Back to Projects</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = project.images?.length > 0 
    ? project.images 
    : ["https://images.unsplash.com/photo-1758548157126-e4c0477f796e?w=800"];

  return (
    <div data-testid="project-detail-page">
      {/* Back Button */}
      <section className="py-6 bg-gray-50">
        <div className="container-custom">
          <Link 
            to="/projects" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
            data-testid="back-to-projects"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Link>
        </div>
      </section>

      {/* Project Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden mb-4">
                <img
                  src={images[currentImage]}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  data-testid="project-main-image"
                />
              </div>
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        currentImage === index ? "border-primary" : "border-transparent"
                      }`}
                      data-testid={`project-thumbnail-${index}`}
                    >
                      <img
                        src={img}
                        alt={`${project.title} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Project Details */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
                {project.title}
              </h1>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span>{project.location}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Grid3X3 className="w-5 h-5 text-primary" />
                  <span>{project.tile_type}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Ruler className="w-5 h-5 text-primary" />
                  <span>{project.area_tiled}</span>
                </div>
              </div>

              <div className="prose prose-lg text-gray-600 mb-8">
                <p>{project.description}</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-gray-900 mb-4 font-montserrat">Project Details</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Location</dt>
                    <dd className="font-medium text-gray-900">{project.location}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Tile Type</dt>
                    <dd className="font-medium text-gray-900">{project.tile_type}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Area Tiled</dt>
                    <dd className="font-medium text-gray-900">{project.area_tiled}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Category</dt>
                    <dd className="font-medium text-gray-900 capitalize">{project.category?.replace("_", " ")}</dd>
                  </div>
                </dl>
              </div>

              <Link to="/quote">
                <Button size="lg" className="w-full bg-primary hover:bg-primary/90" data-testid="project-detail-cta">
                  Get a Quote For Similar Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetailPage;
